import React from 'react';
import {
    Box,
    Typography,
    Accordion,
    AccordionSummary,
    AccordionDetails,
    Button,
    CircularProgress,
    TextField,
    Alert,
    alpha,
} from '@mui/material';
import { ExpandMore, Add, School, SportsBaseball, Refresh } from '@mui/icons-material';
import useTheme from '@mui/system/useTheme';
import type {
    BaseballRecruitProfile,
    UnmatchedCollegePlayer,
    UnmatchedSummerPlayer,
} from 'api/BaseballRecruits';

export interface ImportStatsSectionProps {
    newUrl: string;
    onNewUrlChange: (url: string) => void;
    adding: boolean;
    onAddUrl: () => void;
    isValidMaxprepsUrl: (url: string) => boolean;
    collegeStatsUrl: string;
    onCollegeStatsUrlChange: (url: string) => void;
    importingCollegeStats: boolean;
    onImportCollegeStats: () => void;
    refreshingCollegeStats: boolean;
    isActive: boolean;
    onRefreshCollegeStats: () => void;
    profilesWithCollegeStats: BaseballRecruitProfile[];
    latestCollegeScrape: string | null | undefined;
    collegeStatsUnmatched: UnmatchedCollegePlayer[];
    onOpenCollegeStatsMatch: () => void;
    summerStatsUrl: string;
    onSummerStatsUrlChange: (url: string) => void;
    importingSummerStats: boolean;
    onImportSummerStats: () => void;
    summerStatsUnmatched: UnmatchedSummerPlayer[];
    onOpenSummerStatsMatch: () => void;
}

const ImportStatsSection: React.FC<ImportStatsSectionProps> = (props) => {
    const theme = useTheme();
    const {
        newUrl,
        onNewUrlChange,
        adding,
        onAddUrl,
        isValidMaxprepsUrl,
        collegeStatsUrl,
        onCollegeStatsUrlChange,
        importingCollegeStats,
        onImportCollegeStats,
        refreshingCollegeStats,
        isActive,
        onRefreshCollegeStats,
        profilesWithCollegeStats,
        latestCollegeScrape,
        collegeStatsUnmatched,
        onOpenCollegeStatsMatch,
        summerStatsUrl,
        onSummerStatsUrlChange,
        importingSummerStats,
        onImportSummerStats,
        summerStatsUnmatched,
        onOpenSummerStatsMatch,
    } = props;

    return (
        <Box
            sx={{
                border: '1px solid',
                borderColor: alpha(theme.palette.primary.main, 0.2),
                borderRadius: 2,
                p: 1.5,
                background: alpha(theme.palette.primary.main, 0.02),
                order: { xs: 2, md: 1 },
            }}
        >
            <Typography
                variant="overline"
                sx={{
                    color: 'primary.main',
                    fontWeight: 700,
                    textAlign: 'center',
                    mb: 1,
                    letterSpacing: 1,
                    fontSize: '0.85rem',
                }}
            >
                Import Stats
            </Typography>
            <Accordion
                disableGutters
                elevation={0}
                sx={{
                    border: '1px solid',
                    borderColor: 'divider',
                    borderRadius: '8px !important',
                    '&:before': { display: 'none' },
                    mb: 1,
                }}
            >
                <AccordionSummary expandIcon={<ExpandMore />}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <img
                            src="/maxpreps-logo.png"
                            alt="MaxPreps"
                            style={{ height: 20, objectFit: 'contain' }}
                        />
                        <Typography variant="subtitle2" color="primary.main" fontWeight={700}>
                            Imported Player Stats
                        </Typography>
                    </Box>
                </AccordionSummary>
                <AccordionDetails sx={{ pt: 0 }}>
                    <Box sx={{ display: 'flex', gap: 1 }}>
                        <TextField
                            size="small"
                            fullWidth
                            placeholder="https://www.maxpreps.com/athlete/.../baseball/stats"
                            value={newUrl}
                            onChange={(e) => onNewUrlChange(e.target.value)}
                            onKeyDown={(e) => {
                                if (e.key === 'Enter') onAddUrl();
                            }}
                            disabled={adding}
                        />
                        <Button
                            variant="contained"
                            color="primary"
                            onClick={onAddUrl}
                            disabled={adding || !newUrl.trim() || !isValidMaxprepsUrl(newUrl)}
                            startIcon={adding ? <CircularProgress size={16} /> : <Add />}
                            sx={{ whiteSpace: 'nowrap' }}
                        >
                            Import
                        </Button>
                    </Box>
                </AccordionDetails>
            </Accordion>
            <Accordion
                disableGutters
                elevation={0}
                sx={{
                    border: '1px solid',
                    borderColor: 'divider',
                    borderRadius: '8px !important',
                    '&:before': { display: 'none' },
                    mb: 1,
                }}
            >
                <AccordionSummary expandIcon={<ExpandMore />}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <School sx={{ color: 'primary.main' }} fontSize="small" />
                        <Typography variant="subtitle2" color="primary.main" fontWeight={700}>
                            College Stats
                        </Typography>
                    </Box>
                </AccordionSummary>
                <AccordionDetails sx={{ pt: 0 }}>
                    <Box sx={{ display: 'flex', gap: 1 }}>
                        <TextField
                            size="small"
                            fullWidth
                            placeholder="https://thesundevils.com/sports/baseball/stats"
                            value={collegeStatsUrl}
                            onChange={(e) => onCollegeStatsUrlChange(e.target.value)}
                            onKeyDown={(e) => {
                                if (e.key === 'Enter') onImportCollegeStats();
                            }}
                            disabled={importingCollegeStats}
                        />
                        <Button
                            variant="contained"
                            color="primary"
                            onClick={onImportCollegeStats}
                            disabled={importingCollegeStats || !collegeStatsUrl.trim()}
                            startIcon={
                                importingCollegeStats ? <CircularProgress size={16} /> : <School />
                            }
                            sx={{ whiteSpace: 'nowrap' }}
                        >
                            Import Stats
                        </Button>
                    </Box>
                    {profilesWithCollegeStats.length > 0 && (
                        <Box
                            sx={{
                                mt: 1.5,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'space-between',
                            }}
                        >
                            <Typography variant="caption" color="text.secondary">
                                {profilesWithCollegeStats.length} player(s) with college stats
                                {latestCollegeScrape && (
                                    <>
                                        {' '}
                                        &middot; Last updated{' '}
                                        {new Date(latestCollegeScrape).toLocaleDateString()}
                                    </>
                                )}
                            </Typography>
                            <Button
                                size="small"
                                variant="outlined"
                                color="primary"
                                onClick={onRefreshCollegeStats}
                                disabled={refreshingCollegeStats || isActive}
                                startIcon={
                                    refreshingCollegeStats ? (
                                        <CircularProgress size={14} />
                                    ) : (
                                        <Refresh />
                                    )
                                }
                            >
                                Refresh Stats
                            </Button>
                        </Box>
                    )}
                    {collegeStatsUnmatched.length > 0 && (
                        <Alert
                            severity="warning"
                            sx={{ mt: 2 }}
                            action={
                                <Button
                                    color="warning"
                                    size="small"
                                    onClick={() => onOpenCollegeStatsMatch()}
                                >
                                    Review &amp; Match
                                </Button>
                            }
                        >
                            <Typography variant="body2" fontWeight={600}>
                                {collegeStatsUnmatched.length} player(s) need manual matching
                            </Typography>
                        </Alert>
                    )}
                </AccordionDetails>
            </Accordion>
            <Accordion
                disableGutters
                elevation={0}
                sx={{
                    border: '1px solid',
                    borderColor: 'divider',
                    borderRadius: '8px !important',
                    '&:before': { display: 'none' },
                }}
            >
                <AccordionSummary expandIcon={<ExpandMore />}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <SportsBaseball sx={{ color: 'primary.main' }} fontSize="small" />
                        <Typography variant="subtitle2" color="primary.main" fontWeight={700}>
                            Baseball Reference Stats
                        </Typography>
                    </Box>
                </AccordionSummary>
                <AccordionDetails sx={{ pt: 0 }}>
                    <Box sx={{ display: 'flex', gap: 1 }}>
                        <TextField
                            size="small"
                            fullWidth
                            placeholder="https://www.baseball-reference.com/register/player.fcgi?id=..."
                            value={summerStatsUrl}
                            onChange={(e) => onSummerStatsUrlChange(e.target.value)}
                            onKeyDown={(e) => {
                                if (e.key === 'Enter') onImportSummerStats();
                            }}
                            disabled={importingSummerStats}
                        />
                        <Button
                            variant="contained"
                            color="primary"
                            onClick={onImportSummerStats}
                            disabled={importingSummerStats || !summerStatsUrl.trim()}
                            startIcon={
                                importingSummerStats ? (
                                    <CircularProgress size={16} />
                                ) : (
                                    <SportsBaseball />
                                )
                            }
                            sx={{ whiteSpace: 'nowrap' }}
                        >
                            Import Stats
                        </Button>
                    </Box>
                    {summerStatsUnmatched.length > 0 && (
                        <Alert
                            severity="warning"
                            sx={{ mt: 2 }}
                            action={
                                <Button
                                    color="warning"
                                    size="small"
                                    onClick={() => onOpenSummerStatsMatch()}
                                >
                                    Review &amp; Match
                                </Button>
                            }
                        >
                            <Typography variant="body2" fontWeight={600}>
                                {summerStatsUnmatched.length} player(s) need manual matching
                            </Typography>
                        </Alert>
                    )}
                </AccordionDetails>
            </Accordion>
        </Box>
    );
};

export default ImportStatsSection;
